import type { ReactElement, ReactNode } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * The design system's hover text, in place of a native `title`.
 *
 * A browser title is unstyled, arrives late and is cut wherever the window
 * decides, so the product never uses one. This wraps one element and shows
 * `content` beside it on hover and on keyboard focus.
 *
 * It brings its own provider, so it works on any render path, a test included,
 * without the page having to remember one. With no `content` it renders the
 * child untouched, which lets a caller pass an optional reason straight in.
 *
 * - The child must take a ref and props (a DOM element or a forwardRef
 *   component), because the trigger is merged onto it.
 * - A disabled control gets no pointer events, so wrap it in a
 *   `<span tabIndex={0}>` and give the hint to the span.
 * - Hover text is not a name. An icon-only button still needs its own
 *   `aria-label`.
 */
export function Hint({
  content,
  children,
  side = "top",
  align = "center",
  className,
}: {
  content: ReactNode;
  children: ReactElement;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  className?: string;
}) {
  if (content == null || content === false || content === "") return children;
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent
          side={side}
          align={align}
          className={cn("max-w-xs break-words text-xs", className)}
        >
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
